import type { FunnelParams, FunnelParamsPatch } from '@/model/types';
import { PersistenceError, type PersistencePort } from './types';

const PREFIX = 'dsfe-funnel:scenario:';

function key(scenarioId: string): string {
  return `${PREFIX}${scenarioId}`;
}

/**
 * Local-only persistence. This is the default when no API base URL is
 * configured, and it is a legitimate production mode: the tool holds zero PHI,
 * only synthetic aggregate assumptions, so a browser-local scenario is safe.
 */
export function createLocalAdapter(): PersistencePort {
  function write(scenarioId: string, params: FunnelParams): void {
    try {
      localStorage.setItem(key(scenarioId), JSON.stringify(params));
    } catch (cause) {
      // Private mode or a full quota. Non-fatal: the canvas still works, the
      // edits just will not survive a reload.
      throw new PersistenceError('Could not write scenario to local storage', cause);
    }
  }

  return {
    kind: 'local',

    async load(scenarioId) {
      try {
        const raw = localStorage.getItem(key(scenarioId));
        if (!raw) return { status: 'absent' };
        return { status: 'found', params: JSON.parse(raw) as FunnelParamsPatch };
      } catch {
        // Corrupt, or storage unreadable in private mode. Not the same as "no
        // such scenario": we could not read, so say so and let the caller open.
        return { status: 'unavailable' };
      }
    },

    async save(scenarioId, params) {
      write(scenarioId, params);
    },

    flush(scenarioId, params) {
      try {
        write(scenarioId, params);
      } catch {
        /* pagehide is not a place to raise */
      }
    },
  };
}
